/* Background music: CC0 loops by RandomMind (OpenGameArt.org), see
 * public/music/LICENSE-randommind-medieval.txt.
 *
 * Tracks are fetched and decoded lazily, then looped gaplessly via
 * AudioBufferSourceNode. Scene changes crossfade; the table scene plays
 * quieter so the foley stays in front. Browsers block audio until the first
 * user gesture, so `armMusicAutostart` retries on pointer/key input. */
import { ac, musicBus } from './audio';

export type MusicScene = 'tavern' | 'table';

const TRACKS: Record<MusicScene, readonly string[]> = {
  tavern: ['/music/tavern-old-tower-inn.ogg', '/music/tavern-minstrel-dance.ogg'],
  table: ['/music/table-bards-tale.ogg'],
};
const SCENE_LEVEL: Record<MusicScene, number> = { tavern: 1, table: 0.55 };
const FADE_SECONDS = 1.6;

interface PlayingTrack {
  scene: MusicScene;
  src: AudioBufferSourceNode;
  gain: GainNode;
}

const buffers = new Map<string, Promise<AudioBuffer | null>>();
let current: PlayingTrack | null = null;
let desired: MusicScene | null = null;
let pending: MusicScene | null = null; // a load for this scene is in flight
let tavernIndex = 0; // rotates through the tavern playlist on each visit
let startToken = 0;

function loadBuffer(url: string): Promise<AudioBuffer | null> {
  let p = buffers.get(url);
  if (!p) {
    p = (async () => {
      const c = ac();
      if (!c) return null;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await c.decodeAudioData(await res.arrayBuffer());
      } catch {
        return null; // no music is fine; sfx keep working
      }
    })();
    buffers.set(url, p);
  }
  return p;
}

function fadeOutCurrent(): void {
  if (!current) return;
  const playing = current;
  current = null;
  const c = ac();
  if (!c) {
    try { playing.src.stop(); } catch { /* already stopped */ }
    return;
  }
  const t = c.currentTime;
  playing.gain.gain.cancelScheduledValues(t);
  playing.gain.gain.setTargetAtTime(0, t, FADE_SECONDS / 4);
  try { playing.src.stop(t + FADE_SECONDS); } catch { /* already stopped */ }
}

async function startScene(scene: MusicScene): Promise<void> {
  if (pending === scene) return;
  const token = ++startToken;
  const c = ac();
  const bus = musicBus();
  if (!c || !bus || c.state !== 'running') return; // waits for the first gesture
  const list = TRACKS[scene];
  const url = scene === 'tavern' ? list[tavernIndex++ % list.length]! : list[0]!;
  pending = scene;
  const buf = await loadBuffer(url);
  pending = null;
  if (!buf || token !== startToken || desired !== scene) return;
  fadeOutCurrent();
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const gain = c.createGain();
  const t = c.currentTime;
  gain.gain.setValueAtTime(0, t);
  gain.gain.setTargetAtTime(SCENE_LEVEL[scene], t, FADE_SECONDS / 4);
  src.connect(gain);
  gain.connect(bus);
  src.start();
  current = { scene, src, gain };
}

export function setMusicScene(scene: MusicScene): void {
  desired = scene;
  if (current?.scene === scene) return;
  void startScene(scene);
}

/** Retries the desired scene once the browser allows audio (first gesture). */
export function armMusicAutostart(): void {
  const kick = (): void => {
    if (desired && current?.scene !== desired) void startScene(desired);
  };
  window.addEventListener('pointerdown', kick, { passive: true });
  window.addEventListener('keydown', kick);
}
